import { Result } from '../../../Shared/Protocol/protocol-result'
import { HCPFlowService } from '../../../Services/HCP/flows/hcp-flow.service'
import { IMoveHCPFlowCardPayload, IMoveHCPFlowCardsPayload } from '../../../Services/HCP/flows/hcp-flow.interface'
import { IHCPFlowKanbanCard } from '../../../Services/HCP/flows/hcp-flow-card.interface'
import { getMoveTargetProductionBatchIds } from '../../../Services/HCP/flows/hcp-flow-drag.helpers'

async function runWithConcurrency<T>(
  tasks: Array<() => Promise<T>>,
  concurrency: number
): Promise<T[]> {
  const results: T[] = []

  for (let index = 0; index < tasks.length; index += concurrency) {
    const batch = tasks.slice(index, index + concurrency)
    const batchResults = await Promise.all(batch.map((task) => task()))
    results.push(...batchResults)
  }

  return results
}

export class MoveHCPFlowCardUseCase {
  constructor(private readonly hcpFlowService: HCPFlowService) {}

  public async handle(payload: IMoveHCPFlowCardPayload): Promise<Result<IHCPFlowKanbanCard[]>> {
    try {
      const { flow, statusSequence, hcpFlowStatusId, productionBatchIds, card } = payload

      if (card?.type === 'ORDER') {
        if (!hcpFlowStatusId) return Result.fail(new Error('Status de destino não informado.'))
        const result = await this.hcpFlowService.updateGroupedLogisticsOrderPosition(card.id, hcpFlowStatusId)
        if (result.isFailure) return Result.fail(result.error as Error)
        return Result.ok([card])
      }

      if (card?.type === 'HCP_PRE_SERVICE_ORDER') {
        const result = await this.hcpFlowService.updatePreServiceOrderFlowPosition(card.id, {
          flow,
          statusSequence,
          hcpFlowStatusId,
        })

        if (result.isFailure) {
          return Result.fail(result.error as Error)
        }

        return Result.ok([])
      }

      if (!productionBatchIds.length) {
        return Result.fail(new Error('Nenhum item encontrado para movimentar.'))
      }

      const results = await Promise.all(
        productionBatchIds.map((productionBatchId) =>
          this.hcpFlowService.updateFlowPosition(productionBatchId, { flow, statusSequence, hcpFlowStatusId })
        )
      )

      const failed = results.find((result) => !result.isSuccess)
      if (failed) {
        return Result.fail(failed.error as Error)
      }

      // Mutation responses are raw batches; the caller reloads the Kanban projection.
      return Result.ok([])
    } catch (error: any) {
      return Result.fail(error)
    }
  }

  public async handleMany(payload: IMoveHCPFlowCardsPayload): Promise<Result<IHCPFlowKanbanCard[]>> {
    try {
      const {
        flow,
        statusSequence,
        hcpFlowStatusId,
        cards,
        concurrency = 4,
      } = payload

      if (!cards.length) {
        return Result.fail(new Error('Nenhum card encontrado para movimentar.'))
      }

      if (cards.some((card) => card.type === 'ORDER') && !hcpFlowStatusId) {
        return Result.fail(new Error('Status de destino não informado.'))
      }

      const tasks = cards.flatMap((card) => {
        if (card.type === 'ORDER') {
          return [async () => this.hcpFlowService.updateGroupedLogisticsOrderPosition(card.id, hcpFlowStatusId!)]
        }

        if (card.type === 'HCP_PRE_SERVICE_ORDER') {
          return [async () => this.hcpFlowService.updatePreServiceOrderFlowPosition(card.id, {
            flow,
            statusSequence,
            hcpFlowStatusId,
          })]
        }

        const targetIds = getMoveTargetProductionBatchIds(card)
        return targetIds.map((orderProductId) => (
          async () => this.hcpFlowService.updateFlowPosition(orderProductId, {
            flow,
            statusSequence,
            hcpFlowStatusId,
          })
        ))
      })

      if (!tasks.length) {
        return Result.fail(new Error('Nenhum item encontrado para movimentar.'))
      }

      const results = await runWithConcurrency(tasks, Math.max(1, concurrency))
      const failed = results.find((result) => !result.isSuccess)
      if (failed) {
        return Result.fail(failed.error as Error)
      }

      return Result.ok([])
    } catch (error: any) {
      return Result.fail(error)
    }
  }
}
